import {useLocation} from 'react-router-dom';
import {useAppSelector} from './store';
import {isBarberLoggedIn} from '../slices/authSlice';

// todo extract
interface User {
    id: string;
    // todo roles would be array of...
    roles?: string[];
}

const ROUTES_WITHOUT_SIDE_MENU = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/trending',
    '/explore',
    '/pricing',
    '/bookingTest',
];

// Barber sidebar shows for /barber/* + /dashboard when barber is logged in.
// We key off the Redux flag (not the /me query) so the sidebar doesn't blink
// on first load while barberUser is fetching.
//
// `showSideMenu` controls the dashboard sidebar. `useBarberChrome` controls
// whether we render the BarberTopBar (as opposed to PublicTopBar). The two
// diverge on /barber/settings: settings replaces the dashboard sidebar with
// its own internal rail, but still uses the barber topbar.
export const useSideMenuVisibility = (currentUser: User | undefined) => {
    const location = useLocation();
    const isBarberLogged = useAppSelector(isBarberLoggedIn);

    // Plain predicates, not hooks — they don't call any React hooks
    // themselves. Renamed off the `use*` convention so the rules-of-hooks
    // lint doesn't (correctly) flag them as conditional hook calls.
    const computeBarberChrome = (): boolean => {
        if (!isBarberLogged) return false;
        if (
            location.pathname.startsWith('/r/') ||
            location.pathname.startsWith('/reservation-confirmation/') ||
            location.pathname.startsWith('/customer/') ||
            location.pathname.startsWith('/barber/kiosk') ||
            // Platform admin owns its full layout — see AdminPlatformLayout.
            location.pathname.startsWith('/admin') ||
            // Onboarding wizard ships its own topbar + full-width layout.
            location.pathname.startsWith('/onboarding')
        ) {
            return false;
        }
        return !ROUTES_WITHOUT_SIDE_MENU.includes(location.pathname);
    };

    const computeShowSideMenu = (): boolean => {
        if (!computeBarberChrome()) return false;
        // Settings page replaces the dashboard sidebar with its own settings rail.
        if (location.pathname.startsWith('/barber/settings')) return false;
        return true;
    };

    return {
        showSideMenu: computeShowSideMenu(),
        useBarberChrome: computeBarberChrome(),
        userRoles: currentUser?.roles,
    };
};
